import { AuxButtonSizeEnum, AuxButtonTypeEnum } from "@blk/aladdin-web-components";
import React from "react";
import { batch, useDispatch, useSelector } from "react-redux";
import { useGetDatapointsDataWithFilters } from "../hooks/useGetDatapointsDataWithFilters";
import { getMeasuresAndDimensions, useCustomDatapointDimensions } from "../portfolio-creation/add-investor-accounts-or-funds-modal/helper";
import { InsGridWidget } from "components/widgets";
import { InsGridAggs } from "components/widgets/grid/grid-agg-funcs";
import { CustomColumnType } from "components/widgets/grid/grid-column-types";
import { getDefaultMappings, insDatapointGridWidgetAdapterData } from "components/widgets/grid/insDatapointGridWidgetAdapter";
import { appInsightEvents, language } from "language/default";
import { appInsightsTrackEvent } from "plugins/app-insight-init";
import { removeEditedRow, removeSelectedRow, setEditedRow } from "store/grids";
import { AddInvestorAccountsOrFundsMode } from "store/modal/portfolio-creation-types";
import { getCurrentLocalTime } from "common/date-utils";
export const defaultCommitmentValue = { amount: 1000000 };
const valueSetter = (params) => {
    // If the number is percent fomatted X.X%, scale to TotalCommitment
    const match = String(params.newValue).match(/^(?<percent>(?<a>\d+)(?<b>\.\d+)?)%$/u);
    if (!isNaN(Number(match?.groups?.percent))) {
        params.data.CustomCommitment = {
            amount: Number((params.data.Commitment?.amount ?? defaultCommitmentValue.amount) * Number(match?.groups?.percent) / 100)
        };
        return true;
    }
    else if (params.newValue === "") {
        params.data.CustomCommitment = null;
        return true;
    }
    else if (!isNaN(Number(params.newValue))) {
        if (params.newValue < 0) {
            return false;
        }
        // Use Number() to display 0 if empty string
        params.data.CustomCommitment = { amount: Number(params.newValue) };
        return true;
    }
    return false;
};
const valueGetter = (params) => {
    const amount = params.data.editedRow?.Commitment?.amount ?? "";
    return {
        amount,
        currency: params.data.Currency
    };
};
export const getCommitment = (data) => {
    if (!data) {
        return {};
    }
    return !data.InvestorId && !data.CustomCommitment ? data.FundSize : data.CustomCommitment;
};
export const portfolioCreationManualCompositionGridId = "portfolio-creation-manual-composition-grid";
export const useManualSelectionSelectedRows = () => useSelector((state) => state.grids.state[portfolioCreationManualCompositionGridId]?.selectedRows);
export const useManualSelectionEditRows = () => useSelector((state) => state.grids.state[portfolioCreationManualCompositionGridId]?.editedRows);
export const useGetFundAndInvestorAccountRows = (cols) => {
    const listOfCustomFieldsFund = useCustomDatapointDimensions(AddInvestorAccountsOrFundsMode.AddFunds);
    const { dimensions: dimensionsFund, measures: measuresFund } = getMeasuresAndDimensions(listOfCustomFieldsFund, AddInvestorAccountsOrFundsMode.AddFunds);
    const [dataFund, queryStateFund, refreshFund] = useGetDatapointsDataWithFilters(dimensionsFund, measuresFund, portfolioCreationManualCompositionGridId, false, AddInvestorAccountsOrFundsMode.AddFunds);
    const listOfCustomFieldsInvestorAccount = useCustomDatapointDimensions(AddInvestorAccountsOrFundsMode.AddInvestorAccounts);
    const { dimensions: dimensionsInvestorAccount, measures: measuresInvestorAccount } = getMeasuresAndDimensions(listOfCustomFieldsInvestorAccount, AddInvestorAccountsOrFundsMode.AddInvestorAccounts);
    const [dataInvestorAccount, queryStateInvestorAccount, refreshInvestorAccount] = useGetDatapointsDataWithFilters(dimensionsInvestorAccount, measuresInvestorAccount, portfolioCreationManualCompositionGridId, false, AddInvestorAccountsOrFundsMode.AddInvestorAccounts);
    const refresh = () => refreshFund().then(() => refreshInvestorAccount());
    const { rows: fundRows } = insDatapointGridWidgetAdapterData(dataFund, cols);
    const { rows: investorRows } = insDatapointGridWidgetAdapterData(dataInvestorAccount, cols);
    const rows = fundRows.concat(investorRows);
    const defaultLinksMapping = getDefaultMappings([
        { dimensions: dimensionsFund, id: "", measures: measuresFund },
        { dimensions: dimensionsInvestorAccount, id: "", measures: measuresInvestorAccount }
    ], cols);
    const queryState = {
        isFinished: queryStateFund?.isFinished && queryStateInvestorAccount?.isFinished,
        isPending: queryStateFund?.isPending || queryStateInvestorAccount?.isPending
    };
    return [rows, queryState, refresh, defaultLinksMapping];
};
export const InsPortfolioCreationManualCompositionGrid = () => {
    const dispatch = useDispatch(), selectedRows = useManualSelectionSelectedRows() ?? [];
    const editedRows = useManualSelectionEditRows() ?? {};
    const cols = [
        { field: "Id", hide: true, lockVisible: true },
        { field: "Name", headerName: language.en.modal.portfolioCreation.grid.fundName, sort: "asc", type: [CustomColumnType.AUX_TEXT_COLUMN] },
        { field: "Currency", headerName: language.en.modal.portfolioCreation.grid.fundCurrency, type: CustomColumnType.AUX_TEXT_COLUMN },
        { field: "InvestorName", headerName: language.en.modal.portfolioCreation.grid.investorAccount, type: CustomColumnType.AUX_TEXT_COLUMN },
        { field: "InvestorCurrency", headerName: language.en.modal.portfolioCreation.grid.investorCurrency, type: CustomColumnType.InsTextSetColumn },
        { field: "Commitment", headerName: language.en.modal.portfolioCreation.grid.commitment, type: CustomColumnType.InsMoneyColumn },
        {
            aggFunc: InsGridAggs.totalData,
            cellClass: ["aux-editable-cell"],
            cellEditorParams: {
                useFormatter: true
            },
            cellEditorSelector: () => ({ component: "agTextCellEditor" }),
            editable: true,
            field: "CustomCommitment",
            headerName: language.en.modal.portfolioCreation.grid.customCommitment,
            onCellValueChanged: (event) => {
                if (event.data.CustomCommitment?.amount < 0) {
                    return;
                }
                let key = event.data.Id;
                if (event.data?.InvestorId) {
                    key += event.data.InvestorId;
                }
                appInsightsTrackEvent({ name: appInsightEvents.customEvents.names.CreatePortfolioChangeScaling, properties: { LocalTimestamp: getCurrentLocalTime() } });
                dispatch(setEditedRow({
                    id: portfolioCreationManualCompositionGridId,
                    key,
                    value: {
                        Commitment: getCommitment(event.data),
                        CustomCommitment: event.data.CustomCommitment
                    }
                }));
            },
            singleClickEdit: true,
            type: [CustomColumnType.InsMoneyColumn, CustomColumnType.InsEditableColumn],
            valueFormatter: (params) => params.value.amount,
            valueGetter,
            valueSetter
        },
        { field: "Commitment Date", headerName: language.en.modal.portfolioCreation.grid.commitmentDate, type: [CustomColumnType.InsDateColumn] },
        { colId: "Delete", field: "deleteIcon", headerName: " ", type: [CustomColumnType.AUX_BUTTON_COLUMN, CustomColumnType.InsCenterAllignedColumn], width: 32 }
    ];
    const [rows, queryState, refresh, defaultMappings] = useGetFundAndInvestorAccountRows(cols);
    if (!selectedRows?.length) {
        return React.createElement("div", { className: "centered-content" }, language.en.modal.portfolioCreation.portfolioCreationManualComposition.addAssets);
    }
    const handleRemoveRow = (rowId) => {
        batch(() => {
            dispatch(removeEditedRow({ id: portfolioCreationManualCompositionGridId, row: rowId }));
            dispatch(removeSelectedRow({ id: portfolioCreationManualCompositionGridId, row: rowId }));
        });
    };
    const rowsToDisplay = selectedRows.map((sr) => {
        const row = rows.find((r) => (sr === r.Id && !r.InvestorId) || sr === `${r.Id}-${r.InvestorId}`);
        if (!row) {
            return undefined;
        }
        let id = row.Id;
        if (row.InvestorId) {
            id += row.InvestorId;
        }
        return {
            ...row,
            deleteIcon: {
                icon: "delete",
                onClick: () => handleRemoveRow(sr),
                size: AuxButtonSizeEnum.SMALL,
                type: AuxButtonTypeEnum.TERTIARY_SUBTLE
            },
            editedRow: editedRows[id]
        };
    })
        .filter((x) => x !== undefined);
    return React.createElement(InsGridWidget, { hideControls: true, selectionMode: "multiple", widgetId: portfolioCreationManualCompositionGridId, stateId: portfolioCreationManualCompositionGridId, columnDefs: cols, rowData: rowsToDisplay, refreshAction: refresh, queryState: queryState, suppressRowClickSelection: true, context: { linksMapping: defaultMappings }, hideFilters: true });
};
